"use client";

import GradientLogoLoading from "@/components/layouts/GradientLogoLoading";
import {
	createContext,
	useContext,
	useState,
	ReactNode,
	useMemo,
} from "react";
import { MuiThemeProvider } from "./ThemeProvider";
import { TelegramProvider } from "./TelegramProvider";
import { Box } from "@mui/material";

interface AppContextValue {
	isLoading: boolean;
	setLoading: (loading: boolean) => void;
}

const AppContext = createContext<AppContextValue>({
	isLoading: true,
	setLoading: () => {},
});

export const useApp = () => useContext(AppContext);

// New context for logo visibility control
interface LogoControlContextValue {
	isLogoVisible: boolean;
	setLogoVisible: (visible: boolean) => void;
}

export const LogoControlContext = createContext<
	LogoControlContextValue | undefined
>(undefined);

export const useLogoControl = () => {
	const context = useContext(LogoControlContext);
	if (!context) {
		throw new Error("useLogoControl must be used within AppProvider");
	}
	return context;
};

export function AppProvider({ children }: { children: ReactNode }) {
	const [isLoading, setLoading] = useState(true);
	const [isLogoVisible, setLogoVisible] = useState(true);

	const logoControlValue = useMemo(
		() => ({ isLogoVisible, setLogoVisible }),
		[isLogoVisible],
	);

	return (
		<AppContext.Provider value={{ isLoading, setLoading }}>
			<MuiThemeProvider>
				<TelegramProvider>
					<LogoControlContext.Provider value={logoControlValue}>
						<Box sx={{ visibility: isLogoVisible ? "visible" : "hidden" }}>
							<GradientLogoLoading isLoading={isLoading} />
						</Box>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "flex-start",
								flex: 1,
								width: "100%",
								opacity: isLoading ? 0 : 1,
								transition: "opacity 0.5s",
							}}
						>
							{children}
						</Box>
					</LogoControlContext.Provider>
				</TelegramProvider>
			</MuiThemeProvider>
		</AppContext.Provider>
	);
}
